import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import {
  maxTriesFor,
  type AttemptResult,
  type Module,
  type Question,
  type SessionScore,
} from '@ezishizi/learning-core';
import type { ChallengeStats } from '../lib/store/index.ts';

export type Phase = 'gate' | 'question' | 'feedback' | 'done';

/** Where the child came from, so Result/exit can send them back to the same screen. */
export type Origin = 'path' | 'set' | 'lesson' | 'home' | 'word' | 'games';

/** One Speak question the Worker persisted, for the result screen's parent fairness block. */
export interface SpeakAttemptSummary {
  speechAttemptId: string;
  character: string;
  outcome: 'pass' | 'retry' | 'wrong' | 'no_speech';
}

export interface LastResult {
  lessonId: string;
  setId: string | null;
  module: Module;
  /** The taught character of a word game, else null. */
  character: string | null;
  /** The minigame format when launched from the Games lab, else null. */
  game: string | null;
  origin: Origin;
  score: SessionScore;
  wrongChars: string[];
  newlyMastered: number;
  /** Speak questions the Worker scored and saved this run, so the result screen can offer a
   * parent fair/unfair label per attempt. Empty/absent when there were none (local store, no
   * Speak questions, or nothing the Worker could persist). */
  speakAttempts?: SpeakAttemptSummary[];
  /** The id of the saved session, which is the key of its job in the save outbox
   * (lib/backgroundSave.ts) — what the result screen looks up to say whether the save failed. */
  sessionId?: string;
}

interface SessionState {
  /** Whose session this is. A persisted run is only ever resumed for the profile that started
   * it: without this, sibling B reopening the same stop after sibling A left mid-run (an iPad
   * swipe-back, a reload, no exit dialog) would pick up A's run and A's answers would be saved
   * against B. */
  profileId: string | null;
  lessonId: string | null;
  setId: string | null;
  module: Module | null;
  /** The taught character when a word game is running (`module` = `word_*`), else null. */
  character: string | null;
  /** The minigame format when the Games lab launched this session, else null. */
  game: string | null;
  origin: Origin;
  /** Test mode: one try per question, no second chance. */
  strict: boolean;
  questions: Question[];
  index: number;
  results: AttemptResult[];
  triesOnCurrent: number;
  /** Answers withheld because the prompt never came out. Kept so the runner can tell a session
   * the child could not hear from one that simply had nothing to grade — a module of
   * introductions scores three stars on an empty results list, and should go on doing so. */
  unheard: number;
  phase: Phase;
  lastCorrect: boolean | null;
  startedAt: number;
  questionShownAt: number;
  lastResult: LastResult | null;
  /** characters chosen for a "retry mistakes" run */
  mistakeChars: string[];
  /** Daily-challenge tracker returned by the background save, for the Result screen; not
   * persisted (it's re-fetched whenever the gate loads) and cleared on every new `start`. */
  challengeStats: ChallengeStats | null;
  /** true when the gate screen was reached by resuming a persisted in-progress session */
  resumable: boolean;
  start: (p: {
    profileId: string;
    lessonId: string;
    setId: string | null;
    module: Module;
    character?: string | null;
    game?: string | null;
    questions: Question[];
    origin?: Origin;
    strict?: boolean;
  }) => void;
  /** Queue more questions after the current list (a wrong answer's re-ask, at the end). */
  append: (qs: Question[]) => void;
  /** Re-enter the gate screen for a session restored from storage, without rebuilding it — unless
   * the persisted `feedback` already has its answer recorded, in which case this skips past that
   * question (to `done`, if it was the last one) exactly as tapping Next would have. */
  resume: () => void;
  begin: () => void;
  answer: (
    correct: boolean,
    opts?: {
      afterRetry?: boolean;
      /** The prompt never came out, so this answer says nothing about the child. The question
       * still closes and the session still moves on; it is simply not written down. */
      unheard?: boolean;
      speechAttemptId?: string;
      speechOutcome?: AttemptResult['speechOutcome'];
    },
  ) => void;
  next: () => void;
  setLastResult: (r: LastResult) => void;
  setMistakeChars: (c: string[]) => void;
  setChallengeStats: (s: ChallengeStats | null) => void;
  reset: () => void;
}

// The two-tries default `maxTriesFor` (learning-core) falls back to when there is no current
// question to look at (an empty question list) — kept in step with it by hand for that one case.
const MAX_TRIES = 2;
const STORAGE_KEY = 'ezishizi:session';

// Guard every call: sessionStorage throws in Safari private mode, and doesn't exist at all in
// non-browser test environments. Either way the app should keep working with no persistence.
const safeSessionStorage: StateStorage = {
  getItem: (name) => {
    try {
      return typeof sessionStorage === 'undefined' ? null : sessionStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(name, value);
    } catch {
      /* private mode / storage full: ignore */
    }
  },
  removeItem: (name) => {
    try {
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(name);
    } catch {
      /* ignore */
    }
  },
};

function clearPersistedSession() {
  safeSessionStorage.removeItem(STORAGE_KEY);
}

export const useSession = create<SessionState>()(
  persist(
    (set, get) => ({
      profileId: null,
      lessonId: null,
      setId: null,
      module: null,
      character: null,
      game: null,
      origin: 'lesson',
      strict: false,
      questions: [],
      index: 0,
      results: [],
      unheard: 0,
      triesOnCurrent: 0,
      phase: 'gate',
      lastCorrect: null,
      startedAt: 0,
      questionShownAt: 0,
      lastResult: null,
      mistakeChars: [],
      challengeStats: null,
      resumable: false,
      start: ({
        profileId,
        lessonId,
        setId,
        module,
        character = null,
        game = null,
        questions,
        origin = 'lesson',
        strict = false,
      }) =>
        set({
          profileId,
          lessonId,
          setId,
          module,
          character,
          game,
          origin,
          strict,
          questions,
          index: 0,
          results: [],
          unheard: 0,
          triesOnCurrent: 0,
          phase: 'gate',
          lastCorrect: null,
          startedAt: Date.now(),
          questionShownAt: Date.now(),
          resumable: false,
          challengeStats: null,
        }),
      resume: () => {
        const s = get();
        // A reload can land here with the feedback for an already-recorded answer still on
        // screen (a right answer, or a wrong one with no tries left). Continue must not re-ask
        // that question: it would be answered a second time, and scoreSession and Play's mastery
        // update would both count the same question twice. Skip past it exactly as `next()`
        // would, then stop at the gate as every other resume does — or land straight on `done`,
        // if it was the last question, so Play's own done-effect scores and saves the run.
        if (s.phase === 'feedback') {
          const q = s.questions[s.index];
          const recorded = !!q && s.results.some((r) => r.questionId === q.id);
          if (recorded) {
            const index = s.index + 1;
            if (index >= s.questions.length) {
              set({ phase: 'done', index: s.index, lastCorrect: null });
              clearPersistedSession();
            } else {
              set({ index, triesOnCurrent: 0, phase: 'gate', lastCorrect: null, resumable: true });
            }
            return;
          }
        }
        // A 'question' phase, or 'feedback' with nothing recorded yet for the current question
        // (a wrong answer with a try left, or an unheard answer): the same question again, tries
        // kept, once the child taps Continue.
        set({ phase: 'gate', lastCorrect: null, resumable: true });
      },
      append: (qs) => set({ questions: [...get().questions, ...qs] }),
      begin: () =>
        set({ phase: get().questions.length ? 'question' : 'done', questionShownAt: Date.now() }),
      answer: (correct, opts) => {
        const s = get();
        const q = s.questions[s.index];
        if (!q || s.phase !== 'question') return;
        const tries = s.triesOnCurrent + 1;
        const exhausted = !correct && tries >= maxTriesFor(q, s.strict);
        // An answer given to a prompt that never played is not evidence either way. Dropping the
        // wrong ones alone would have been worse than useless: with nothing to lose, a child
        // guesses through the tiles and the right one lands as a clean first try, so silence
        // would promote a word they never heard and push its next review out.
        const record = (correct || exhausted) && !opts?.unheard;
        const results = record
          ? [
              ...s.results,
              {
                questionId: q.id,
                type: q.type,
                character: q.entry.char,
                skill: q.skill,
                correct,
                retried: tries > 1 || !!opts?.afterRetry,
                latencyMs: Date.now() - s.questionShownAt,
                ...(opts?.speechAttemptId ? { speechAttemptId: opts.speechAttemptId } : {}),
                ...(opts?.speechOutcome ? { speechOutcome: opts.speechOutcome } : {}),
              },
            ]
          : s.results;
        set({
          results,
          // Only the rows this actually suppressed: a wrong answer with tries left was never
          // going to be written down anyway.
          unheard: opts?.unheard && (correct || exhausted) ? s.unheard + 1 : s.unheard,
          triesOnCurrent: tries,
          phase: 'feedback',
          lastCorrect: correct,
        });
      },
      next: () => {
        const s = get();
        const q = s.questions[s.index];
        // No current question isn't expected outside an empty question list; fall back to the
        // same default `maxTriesFor` uses unstrict (only `strict` itself shortens that).
        const maxTries = q ? maxTriesFor(q, s.strict) : s.strict ? 1 : MAX_TRIES;
        const advance =
          s.lastCorrect === true || s.triesOnCurrent >= maxTries || s.lastCorrect === null;
        if (!advance) {
          set({ phase: 'question', lastCorrect: null, questionShownAt: Date.now() });
          return;
        }
        const index = s.index + 1;
        if (index >= s.questions.length) {
          set({ phase: 'done', index: s.index, lastCorrect: null });
          clearPersistedSession();
        } else {
          set({
            index,
            triesOnCurrent: 0,
            phase: 'question',
            lastCorrect: null,
            questionShownAt: Date.now(),
          });
        }
      },
      setLastResult: (lastResult) => set({ lastResult }),
      setMistakeChars: (mistakeChars) => set({ mistakeChars }),
      setChallengeStats: (challengeStats) => set({ challengeStats }),
      reset: () => {
        set({
          questions: [],
          index: 0,
          results: [],
          phase: 'gate',
          lastCorrect: null,
          resumable: false,
        });
        clearPersistedSession();
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => safeSessionStorage),
      partialize: (s) => ({
        profileId: s.profileId,
        lessonId: s.lessonId,
        setId: s.setId,
        module: s.module,
        character: s.character,
        game: s.game,
        origin: s.origin,
        strict: s.strict,
        questions: s.questions,
        index: s.index,
        results: s.results,
        unheard: s.unheard,
        triesOnCurrent: s.triesOnCurrent,
        startedAt: s.startedAt,
        mistakeChars: s.mistakeChars,
        phase: s.phase,
      }),
    },
  ),
);
